import { api } from '@/lib/axios';

export interface MessageInboxItem {
  messageId: string;
  subject: string | null;
  fromFullName: string | null;
  sentDateTime: string;
  readFlag: boolean;
  preview: string | null;
  relatedAccession: string | null;
  /** Stable conversation root (c_message key). List/overlay identity; open via messageId. */
  conversationRootKey?: number | null;
}

export interface MessageInboxPage {
  pageNumber: number;
  totalCount: number;
  items: MessageInboxItem[];
  /** False when the PPMessagesAccessEnabled flag is off — UI should show the call-center fallback instead of the inbox. */
  messagingEnabled?: boolean;
  callCenterPhone?: string | null;
  callCenterHours?: string | null;
}

export interface MessageRecipient {
  toUserKey: number;
  toUserType: string | null;
  toFullName: string | null;
  toType: string | null;
  readFlag: boolean;
  readDateTime: string | null;
}

export interface ConversationMessage {
  messageId: string;
  body: string | null;
  fromFullName: string | null;
  fromUserKey: number;
  fromUserType: string | null;
  fromGroupCode: string | null;
  fromGroupName: string | null;
  sentDateTime: string;
  isFromPatient: boolean;
}

export interface ConversationPage {
  items: ConversationMessage[];
  hasMoreOlder: boolean;
  oldestMessageId: string | null;
  oldestSentDateTime: string | null;
  pageSize: number;
}

export interface MessageDetail {
  messageId: string;
  subject: string | null;
  body: string | null;
  fromFullName: string | null;
  fromUserKey: number;
  sentDateTime: string;
  relatedAccession: string | null;
  replyingGuid: string | null;
  recipients: MessageRecipient[];
  conversation?: ConversationPage | null;
}

export interface SendMessageRequest {
  subject?: string;
  body: string;
  toUserKeys: number[];
  relatedAccession?: string;
  /** l_message_group.message_group_code — "To" dropdown on the compose form. */
  toGroupCode?: string;
  /** "Portal" | "Email" | "Phone" — follow-up channel the patient selects. */
  followUpType?: string;
  /** Email address or phone when followUpType is "Email" or "Phone". */
  followUpAddress?: string;
}

export interface MessageGroup {
  code: string;
  description: string;
}

export interface SendMessageResponse {
  messageId: string;
}

export const messageService = {
  getInbox: (pageNumber = 1) =>
    api.get<MessageInboxPage>('/Messages/Inbox', { params: { pageNumber } }).then(r => r.data),

  getMessage: (messageId: string) =>
    api.get<MessageDetail>(`/Messages/${messageId}`).then(r => r.data),

  getConversation: (messageId: string, before?: string) =>
    api.get<ConversationPage>(`/Messages/${messageId}/Conversation`, {
      params: before ? { before } : undefined,
    }).then(r => r.data),

  send: (request: SendMessageRequest) =>
    api.post<SendMessageResponse>('/Messages/Send', request).then(r => r.data),

  reply: (messageId: string, body: string) =>
    api.post<SendMessageResponse>(`/Messages/${messageId}/Reply`, { body }).then(r => r.data),

  delete: (messageId: string) =>
    api.delete<void>(`/Messages/${messageId}`).then(r => r.data),

  markRead: (messageId: string) =>
    api.put<void>(`/Messages/${messageId}/Read`).then(r => r.data),

  getGroups: () =>
    api.get<MessageGroup[]>('/Messages/Groups').then(r => r.data),
};

export const messageKeys = {
  all: ['messages'] as const,
  // patientKey (authUserMetaAtom.id) must be part of the key — the inbox is scoped to
  // whichever patient the caller is currently authenticated as, and while viewing another
  // patient's records as an authorized delegate that id changes without a page reload.
  // Without it here, the cached home-patient inbox would keep showing after switching.
  inbox: (page = 1, patientKey?: string | null) => [...messageKeys.all, 'inbox', page, patientKey] as const,
  detail: (id: string) => [...messageKeys.all, 'detail', id] as const,
  groups: () => [...messageKeys.all, 'groups'] as const,
};
